/**
 * src/config/categoryTheme.js
 *
 * AUTHORITATIVE VISINVOKE Dashboard category colour system.
 *
 * This is the single source of truth for the bright, vivid category identity
 * that mirrors the DCC. Every Dashboard/customer-portal page consumes these
 * exact values — do not scatter colour literals through unrelated files, and
 * do not substitute approximate colours.
 *
 * The top category navigation colour is the authoritative identity for that
 * entire product family; when the user enters a category page the same colour
 * family continues throughout the page (atmosphere, panels, borders, labels,
 * CTAs, metrics, selected/hover states).
 *
 *   visual        = D&D Image     -> Electric Purple
 *   professional  = Professional  -> Bright Cyan / Blue
 *   battlemaps    = Battlemaps    -> Vivid Orange
 *   battletracker = Combat PRO    -> Emerald Green
 *   rpg           = RPG Worlds    -> Bright Crimson
 *   security      = Security      -> Bright Gold
 *   utilities     = Utilities     -> Electric Blue
 *
 * Values are keyed by the product keys used in config/products.js so the two
 * systems stay aligned.
 */

/* ── Global dark foundation ────────────────────────────────────────────────
 * The strong category colours require a genuinely dark base. Do not brighten
 * the whole site — the near-black foundation is what makes the colours read as
 * luminous rather than cheap. Body text is never turned grey enough to look
 * disabled.
 */
export const FOUNDATION = {
  bg:          '#080B11', // main background
  bg2:         '#0B0F16', // secondary background
  panel:       '#111722', // standard neutral panel
  panelRaised: '#151C29', // raised neutral panel
  textPrimary: '#F7F9FC',
  textSecondary: '#C7CFDC',
  textMuted:   '#8F9AAC',
  border:      '#263247', // subtle neutral border
};

/* ── Semantic colours ──────────────────────────────────────────────────────
 * Independent of category identity. Use ONLY when they communicate real state
 * (success / available, warning, error / destructive, disabled). Category
 * identity still owns the primary CTA — do not use semantic green merely
 * because a button says LAUNCH.
 */
export const SEMANTIC = {
  success:      '#19C77A',
  warning:      '#F5B92D',
  error:        '#EF445A',
  disabledBg:   '#293449',
  disabledText: '#AAB3C1',
};

/* ── Authoritative category palettes (exact values) ───────────────────────── */
export const CATEGORY_PALETTES = {
  // 1. D&D IMAGE — ELECTRIC PURPLE
  visual: {
    primary:     '#A347FF',
    bright:      '#B966FF',
    deep:        '#6D24C8',
    surface:     '#21152E',
    surfaceDark: '#17101F',
    border:      '#7F32D8',
    glow:        'rgba(163, 71, 255, 0.28)',
    atmosphere:  'rgba(163, 71, 255, 0.07)',
    text:        '#C78BFF',
    cta:         'linear-gradient(135deg, #8D32F4 0%, #A347FF 55%, #B966FF 100%)',
    ctaHover:    'linear-gradient(135deg, #9D42FF 0%, #B45CFF 55%, #CA86FF 100%)',
  },

  // 2. PROFESSIONAL — BRIGHT CYAN / BLUE
  professional: {
    primary:     '#18BDF2',
    bright:      '#3BD3FF',
    deep:        '#087EA8',
    surface:     '#0D2029',
    surfaceDark: '#0A171D',
    border:      '#159BC9',
    glow:        'rgba(24, 189, 242, 0.27)',
    atmosphere:  'rgba(24, 189, 242, 0.07)',
    text:        '#67DDFF',
    cta:         'linear-gradient(135deg, #119ED2 0%, #18BDF2 55%, #3BD3FF 100%)',
    ctaHover:    'linear-gradient(135deg, #17B5EA 0%, #31CCFF 55%, #67E1FF 100%)',
  },

  // 3. BATTLEMAPS — VIVID ORANGE
  battlemaps: {
    primary:     '#F59A23',
    bright:      '#FFB33F',
    deep:        '#A85B0A',
    surface:     '#281B0D',
    surfaceDark: '#1A1209',
    border:      '#C87918',
    glow:        'rgba(245, 154, 35, 0.27)',
    atmosphere:  'rgba(245, 154, 35, 0.07)',
    text:        '#FFC668',
    cta:         'linear-gradient(135deg, #D77A13 0%, #F59A23 55%, #FFB33F 100%)',
    ctaHover:    'linear-gradient(135deg, #EB8C1A 0%, #FFAA31 55%, #FFC268 100%)',
  },

  // 4. COMBAT PRO — EMERALD GREEN
  battletracker: {
    primary:     '#19C785',
    bright:      '#35E6A3',
    deep:        '#087C56',
    surface:     '#0D241C',
    surfaceDark: '#091812',
    border:      '#159E6D',
    glow:        'rgba(25, 199, 133, 0.27)',
    atmosphere:  'rgba(25, 199, 133, 0.07)',
    text:        '#6AEEBC',
    cta:         'linear-gradient(135deg, #109F6A 0%, #19C785 55%, #35E6A3 100%)',
    ctaHover:    'linear-gradient(135deg, #16B77B 0%, #29D997 55%, #5BF0B9 100%)',
  },

  // 5. RPG WORLDS — BRIGHT CRIMSON
  rpg: {
    primary:     '#E53B63',
    bright:      '#FF5A7E',
    deep:        '#8E1937',
    surface:     '#291218',
    surfaceDark: '#1B0C10',
    border:      '#B72D4D',
    glow:        'rgba(229, 59, 99, 0.27)',
    atmosphere:  'rgba(229, 59, 99, 0.07)',
    text:        '#FF8AA2',
    cta:         'linear-gradient(135deg, #BA284B 0%, #E53B63 55%, #FF5A7E 100%)',
    ctaHover:    'linear-gradient(135deg, #D23357 0%, #F54B72 55%, #FF7A96 100%)',
  },

  // 5b. GAMING — BRIGHT CRIMSON (umbrella category for RPG Worlds + Online
  //     Gaming / Virtual Shop). Shares the established RPG-slot crimson so the
  //     top navigation panel keeps its exact prior appearance; only the label
  //     changes from RPG Worlds to Gaming.
  gaming: {
    primary:     '#E53B63',
    bright:      '#FF5A7E',
    deep:        '#8E1937',
    surface:     '#291218',
    surfaceDark: '#1B0C10',
    border:      '#B72D4D',
    glow:        'rgba(229, 59, 99, 0.27)',
    atmosphere:  'rgba(229, 59, 99, 0.07)',
    text:        '#FF8AA2',
    cta:         'linear-gradient(135deg, #BA284B 0%, #E53B63 55%, #FF5A7E 100%)',
    ctaHover:    'linear-gradient(135deg, #D23357 0%, #F54B72 55%, #FF7A96 100%)',
  },

  // 6. SECURITY — BRIGHT GOLD
  security: {
    primary:     '#F2B91E',
    bright:      '#FFD447',
    deep:        '#A66D05',
    surface:     '#28200D',
    surfaceDark: '#1A1508',
    border:      '#C59012',
    glow:        'rgba(242, 185, 30, 0.27)',
    atmosphere:  'rgba(242, 185, 30, 0.07)',
    text:        '#FFE073',
    cta:         'linear-gradient(135deg, #CA8A0C 0%, #F2B91E 55%, #FFD447 100%)',
    ctaHover:    'linear-gradient(135deg, #DEA00F 0%, #FFC62B 55%, #FFE36D 100%)',
    // On the brightest Security buttons use this rather than white where it
    // produces stronger contrast.
    ctaText:     '#171105',
  },

  // 7. UTILITIES — ELECTRIC BLUE
  utilities: {
    primary:     '#2F7DFF',
    bright:      '#4D98FF',
    deep:        '#174DB8',
    surface:     '#0E1B32',
    surfaceDark: '#091322',
    border:      '#2866CE',
    glow:        'rgba(47, 125, 255, 0.27)',
    atmosphere:  'rgba(47, 125, 255, 0.07)',
    text:        '#78ADFF',
    cta:         'linear-gradient(135deg, #225FD4 0%, #2F7DFF 55%, #4D98FF 100%)',
    ctaHover:    'linear-gradient(135deg, #2D72ED 0%, #438EFF 55%, #70B2FF 100%)',
  },
};

/** Resolve a category theme by product key. Falls back to D&D purple. */
export function getCategoryTheme(key) {
  return CATEGORY_PALETTES[key] || CATEGORY_PALETTES.visual;
}

/**
 * Subtle atmospheric page background for a category. The base stays near-black;
 * the category tint is most visible around the upper page/header region and
 * fades into the dark foundation. Never a solid coloured background.
 */
export function categoryPageBackground(key) {
  const t = getCategoryTheme(key);
  return (
    'radial-gradient(circle at 50% 0%, ' + t.atmosphere + ' 0%, rgba(0,0,0,0) 38%), ' +
    FOUNDATION.bg
  );
}

/**
 * Top category navigation button surface (deep -> primary -> bright).
 * Saturated, clearly coloured, still readable.
 */
export function categoryNavGradient(key) {
  const t = getCategoryTheme(key);
  return (
    'linear-gradient(135deg, ' + t.deep + ' 0%, ' + t.primary + ' 55%, ' + t.bright + ' 100%)'
  );
}

/** "r, g, b" for a hex, for building rgba() strings in inline styles. */
export function categoryHexToRgb(hex) {
  let h = (hex || '#A347FF').replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return '163, 71, 255';
  return r + ', ' + g + ', ' + b;
}

const categoryTheme = {
  FOUNDATION,
  SEMANTIC,
  CATEGORY_PALETTES,
  getCategoryTheme,
  categoryPageBackground,
  categoryNavGradient,
  categoryHexToRgb,
};

export default categoryTheme;
